import { useLocation } from 'react-router-dom';

import { HouseholdType } from '@ndvr/customer-api';

import { getHouseholdTypeLabel } from '../household-utils';

export interface HouseholdRouteTypeResult {
    householdType: HouseholdType;
    baseRoute: string;
    householdTypeLabel: string;
    isHouseholdRoute: boolean;
    isProspectRoute: boolean;
    isOnboardingRoute: boolean;
}

export const useHouseholdRouteType = (): HouseholdRouteTypeResult => {
    const location = useLocation();
    const baseRoute = location.pathname.startsWith('/prospect')
        ? 'prospect'
        : location.pathname.startsWith('/onboarding')
          ? 'onboarding'
          : 'household';
    const householdType = {
        household: HouseholdType.CLIENT,
        prospect: HouseholdType.PROSPECT,
        onboarding: HouseholdType.ONBOARDING,
    }[baseRoute];
    const isHouseholdRoute = householdType !== HouseholdType.PROSPECT;
    const isProspectRoute = householdType === HouseholdType.PROSPECT;

    return {
        householdType,
        baseRoute,
        householdTypeLabel: getHouseholdTypeLabel(householdType),
        isHouseholdRoute,
        isProspectRoute,
        isOnboardingRoute: householdType === HouseholdType.ONBOARDING,
    };
};

export interface HouseholdRoutes {
    HouseholdListPageRoutePath: string;
    PortfolioPageRoutePath: (globalAccountGuid: string, accountGroupGuid: string) => string;
    HouseholdIndividualPageRoutePath: (globalAccountGuid: string, tab?: string) => string;
    AccountPageRoutePath: (globalAccountGuid: string, accountGroupGuid: string, accountGuid: string) => string;
    PortfolioOverviewTabRoutePath: (globalAccountGuid: string) => string;
    OwnersTabRoutePath: (globalAccountGuid: string) => string;
    MembersTabRoutePath: (globalAccountGuid: string) => string;
    DetailsTabRoutePath: (globalAccountGuid: string) => string;
    NotesTabRoutePath: (globalAccountGuid: string) => string;
    TasksTabRoutePath: (globalAccountGuid: string) => string;
    EventsTabRoutePath: (globalAccountGuid: string) => string;
    BillingSetupTabRoutePath: ((globalAccountGuid: string) => string) | null;
    BillingHistoryTabRoutePath: ((globalAccountGuid: string) => string) | null;
    ToolsTabRoutePath: ((globalAccountGuid: string) => string) | null;
    OnboardingTabRoutePath: ((globalAccountGuid: string) => string) | null;
    FilesTabRoutePath: (globalAccountGuid: string) => string;
    TextsThreadRoutePath: (globalAccountGuid: string, msgId?: string) => string;
    MessagesTabRoutePath: (globalAccountGuid: string) => string;
    CallDetailRoutePath: (globalAccountGuid: string, zoomPhoneCallGuid: string) => string;
}

export const useHouseholdRoutePaths = (): HouseholdRoutes => {
    const { isHouseholdRoute, baseRoute } = useHouseholdRouteType();

    return {
        HouseholdListPageRoutePath: `/${baseRoute}`,
        PortfolioPageRoutePath: (globalAccountGuid: string, accountGroupGuid: string) =>
            `/${baseRoute}/${globalAccountGuid}/portfolio/${accountGroupGuid}`,
        HouseholdIndividualPageRoutePath: (globalAccountGuid: string, tab: string = '') =>
            `/${baseRoute}/${globalAccountGuid}${tab ? '/' + tab : ''}`,
        AccountPageRoutePath: (globalAccountGuid: string, accountGroupGuid: string, accountGuid: string) =>
            `/${baseRoute}/${globalAccountGuid}/portfolio/${accountGroupGuid}/account/${accountGuid}`,
        PortfolioOverviewTabRoutePath: (globalAccountGuid: string) => `/${baseRoute}/${globalAccountGuid}/portfolio`,
        OwnersTabRoutePath: (globalAccountGuid: string) => `/${baseRoute}/${globalAccountGuid}/owners`,
        MembersTabRoutePath: (globalAccountGuid: string) => `/${baseRoute}/${globalAccountGuid}/members`,
        DetailsTabRoutePath: (globalAccountGuid: string) => `/${baseRoute}/${globalAccountGuid}/details`,
        NotesTabRoutePath: (globalAccountGuid: string) => `/${baseRoute}/${globalAccountGuid}/notes`,
        TasksTabRoutePath: (globalAccountGuid: string) => `/${baseRoute}/${globalAccountGuid}/tasks`,
        EventsTabRoutePath: (globalAccountGuid: string) => `/${baseRoute}/${globalAccountGuid}/events`,
        BillingSetupTabRoutePath: isHouseholdRoute
            ? (globalAccountGuid: string) => `/${baseRoute}/${globalAccountGuid}/billing-setup`
            : null,
        BillingHistoryTabRoutePath: isHouseholdRoute
            ? (globalAccountGuid: string) => `/${baseRoute}/${globalAccountGuid}/billing-history`
            : null,
        ToolsTabRoutePath: isHouseholdRoute
            ? (globalAccountGuid: string) => `/${baseRoute}/${globalAccountGuid}/tools`
            : null,
        OnboardingTabRoutePath: isHouseholdRoute
            ? (globalAccountGuid: string) => `/${baseRoute}/${globalAccountGuid}/onboarding`
            : null,
        FilesTabRoutePath: (globalAccountGuid: string) => `/${baseRoute}/${globalAccountGuid}/files`,
        TextsThreadRoutePath: (globalAccountGuid: string, msgId?: string) =>
            `/${baseRoute}/${globalAccountGuid}/texts/thread${msgId ? `?msg=${msgId}` : ''}`,
        MessagesTabRoutePath: (globalAccountGuid: string) => `/${baseRoute}/${globalAccountGuid}/messages`,
        CallDetailRoutePath: (globalAccountGuid: string, zoomPhoneCallGuid: string) =>
            `/${baseRoute}/${globalAccountGuid}/calls/${zoomPhoneCallGuid}`,
    };
};
